import * as AbWorkflow from './util/workflow'
import {formatDate} from '../utils/time'
import {generateRecurrenceXml} from '../utils/recurrence'
import {fetchRooms} from './room'
import * as DataV2 from './archibus-v2/DataApi'
import * as ReactGA from 'react-ga'

const getRoomCode = record =>
  `${record.values['rm.bl_id']}${record.values['rm.fl_id']}${
    record.values['rm.rm_id']
  }`

export const createWorkspaceBooking = ({params, bookings}) => {
  return AbWorkflow.callMethod(
    'AbEssentialFacility-HotelingHandler-checkRmstdEmstd',
    [bookings]
  )
    .then(result => {
      if (result.code === 'executed') {
        const {dv_id, dp_id, dayPart, date_start, date_end, recurrence} = params

        return AbWorkflow.callMethod(
          'AbEssentialFacility-HotelingHandler-createBookings',
          [dv_id, dp_id, dayPart, date_start, date_end, recurrence, bookings]
        )
      } else {
        return Promise.reject('Bookings do not match')
      }
    })
    .then(result => {
      if (result.code === 'executed') {
        ReactGA.event({
          category: 'Workspace Booking',
          action: 'Create Workspace Booking'
        })
        return Promise.resolve(result)
      } else {
        return Promise.reject(result.message)
      }
    })
}

export const fetchFirstAvailableFloor = ({blId, emId}) => {
  let today = formatDate(new Date(), 'YYYY-MM-DD')

  // Default parameters
  const params = {
    date_start: today,
    date_end: today,
    dayPart: 0,
    emId,
    bl_id: blId,
    fl_id: ''
  }

  return AbWorkflow.callMethod(
    'AbEssentialFacility-HotelingHandler-searchAvailableSpaces',
    [params, '']
  ).then(result => {
    if (result.code === 'executed') {
      let [record] = result.dataSet.records
      return Promise.resolve(record.values['rm.fl_id'])
    } else {
      return Promise.reject()
    }
  })
}

export const fetchIsRoomAvailable = ({blId, flId, rmId, emId}) => {
  let today = formatDate(new Date(), 'YYYY-MM-DD')

  // Default parameters
  const defaultParams = {
    date_start: today,
    date_end: today,
    dayPart: 0
  }

  const roomParams = {
    bl_id: blId,
    fl_id: flId,
    rm_id: rmId,
    emId
  }

  const params = {
    ...defaultParams,
    ...roomParams
  }

  return AbWorkflow.callMethod(
    'AbEssentialFacility-HotelingHandler-searchAvailableSpaces',
    [params, '']
  ).then(result => {
    if (result.code === 'executed') {
      let isAvailable = false
      let [record] = result.dataSet.records
      if (record) {
        let availSpace = parseInt(record.values['rm.avail_space'])
        isAvailable = availSpace > 0
        return Promise.resolve(isAvailable)
      }
    } else {
      return Promise.reject(result.message)
    }
  })
}

export const fetchAvailableRoomList = async ({search, user, activeFloor}) => {
  const {date_start, date_end, dayPart, dp_id, dv_id, rm_type} = search
  const {blId, flId} = activeFloor
  // Default parameters

  const params = {
    date_start: formatDate(date_start, 'YYYY-MM-DD'),
    date_end: formatDate(date_end, 'YYYY-MM-DD'),
    dayPart,
    emId: user.employee.id,
    bl_id: blId,
    fl_id: flId,
    rm_id: '',
    rm_std: '',
    rm_cat: '',
    rm_type,
    dv_id,
    dp_id
  }

  const {recurrenceType, weekDays, frequencyDay, frequency} = search

  let recurrence = generateRecurrenceXml({
    recurrenceType,
    weekDays,
    frequencyDay,
    frequency
  })

  let availableRooms = {}

  const result = await AbWorkflow.callMethod(
    'AbEssentialFacility-HotelingHandler-searchAvailableSpaces',
    [params, recurrence]
  )
      if (result.code === 'executed') {
        availableRooms = result.dataSet.records.reduce((acc, record) => {
          let roomCode = getRoomCode(record)
          const {values} = record
          acc[roomCode] = {
            roomCode,
            availableSpaces: parseInt(values['rm.avail_space']),
            blId: values['rm.bl_id'],
            flId: values['rm.fl_id'],
            rmId: values['rm.rm_id']
          }
          return acc
        }, {})

        const rooms = await fetchRooms({blId: params.bl_id, flId: params.fl_id})


        let totalSpaces = 0
        let data = Object.keys(availableRooms).map(item => {
          let available = availableRooms[item]
          let room = rooms[item]
          const {availableSpaces, blId, flId, rmId} = available
          const {area, category, rmTypeDesc} = room
          totalSpaces += availableSpaces
          return {area, blId, flId, rmId, availableSpaces, category, rmTypeDesc}
        })
        return {
          availableRooms: data,
          totalSpaces
        }
      } else {
        return {
          availableRooms: {},
          totalSpaces: 0
        }
      }



}

export const fetchWorkspaceAvailableRooms = ({
  blId = '',
  user,
  search = {}
}) => {
  let today = formatDate(new Date(), 'YYYY-MM-DD')

  // Default parameters
  const defaultParams = {
    date_start: today,
    date_end: today,
    dayPart: 0,
    emId: user.employee.id,
    bl_id: blId,
    fl_id: '',
    rm_id: '',
    rm_std: '',
    rm_cat: '',
    rm_type: '',
    dv_id: '',
    dp_id: ''
  }

  const {recurrenceType} = search

  let recurrence = ''

  if (recurrenceType) {
    recurrence = recurrenceType === 'none' ? '' : generateRecurrenceXml(search)
  }

  const params = {...defaultParams, ...search}

  return AbWorkflow.callMethod(
    'AbEssentialFacility-HotelingHandler-searchAvailableSpaces',
    [params, recurrence]
  ).then(result => {
    if (result.code === 'executed') {
      let data = result.dataSet.records.map(record => {
        let roomCode = getRoomCode(record)
        return {
          ...record.values,
          ...{
            roomCode
          }
        }
      })

      let roomsByFloor = data.reduce((acc, curVal) => {
        let fl_id = curVal['rm.fl_id']
        let avail_space = parseInt(curVal['rm.avail_space'])
        let rm_id = curVal['rm.rm_id']
        let bl_id = curVal['rm.bl_id']

        if (acc[fl_id]) {
          acc[fl_id].avail_space += avail_space
          acc[fl_id].roomCodes.push({bl_id, fl_id, rm_id})
        } else {
          acc[fl_id] = {
            avail_space: avail_space,
            roomCodes: [{bl_id, fl_id, rm_id}]
          }
        }
        return acc
      }, {})

      return Promise.resolve(roomsByFloor)
    }
  })
}

export const fetchAvailableRoomsByFloor = ({blId, user}) =>
  fetchWorkspaceAvailableRooms({blId, user})

export const fetchWorkspaceReservations = ({
  emId = '',
  numberOfRecords = 1000
}) => {
  let restriction = new Ab.view.Restriction()
  if (emId) {
    restriction.addClause('activity_log.requestor', emId, '=', 'OR')
    restriction.addClause('activity_log.assigned_to', emId, '=', 'OR')
    return DataV2.getPagedDataFromDataSource(
      'ab-selfservice-dashboard.axvw',
      'workplaceReservations',
      restriction,
      [],
      1,
      numberOfRecords
    )
      .then(result => Promise.resolve(result))
      .catch(error => {
        console.log(error)
      })
  } else {
    return Promise.resolve({data: [], total: 0})
  }
}

export const cancelWorkspaceReservation = reservation => {
  const operationLevel = '1'
  const pctIds = [reservation.pctId]
  const parentPctId = reservation.parentPctId
  const activityLogIds = [reservation.activityLogId]
  const emIds = [reservation.emId]
  const visitorIds = reservation.visitor_id ? [reservation.visitor_id] : ['']
  const blIds = [reservation.blId]

  return AbWorkflow.callMethod(
    'AbEssentialFacility-HotelingHandler-cancelBookings',
    [
      operationLevel,
      pctIds,
      parentPctId,
      emIds,
      visitorIds,
      blIds,
      activityLogIds
    ]
  ).then(({code, message}) => {
    if (code === 'executed') {
      return Promise.resolve()
    } else {
      return Promise.reject(message)
    }
  })
}
